import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Award } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'success', duration = 4000) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type, duration }]);

        // Auto remove
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ addToast }}>
            {children}

            {/* Toast Container */}
            <div className="fixed top-24 right-4 z-50 flex flex-col gap-3 min-w-[320px] max-w-sm pointer-events-none">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`
                            pointer-events-auto transform transition-all duration-300 ease-out animate-slide-in-right
                            flex items-center gap-3 p-4 rounded-xl shadow-xl border
                            ${toast.type === 'milestone' ? 'bg-slate-900 border-indigo-500/50 text-white' : ''}
                            ${toast.type === 'success' ? 'bg-white border-emerald-100 text-slate-800' : ''}
                            ${toast.type === 'error' ? 'bg-white border-rose-100 text-slate-800' : ''}
                        `}
                    >
                        {/* ICON */}
                        <div className={`p-2 rounded-full shrink-0
                            ${toast.type === 'milestone' ? 'bg-indigo-500/20 text-indigo-400' : ''}
                            ${toast.type === 'success' ? 'bg-emerald-100 text-emerald-600' : ''}
                            ${toast.type === 'error' ? 'bg-rose-100 text-rose-600' : ''}
                        `}>
                            {toast.type === 'milestone' && <Award className="w-5 h-5" />}
                            {toast.type === 'success' && <CheckCircle className="w-5 h-5" />}
                            {toast.type === 'error' && <AlertCircle className="w-5 h-5" />}
                        </div>

                        <div className="flex-1">
                            {toast.type === 'milestone' && (
                                <p className="text-xs font-bold text-indigo-300 uppercase tracking-wider mb-0.5">Achievement Unlocked</p>
                            )}
                            <p className={`text-sm font-medium ${toast.type === 'milestone' ? 'text-white' : 'text-slate-700'}`}>
                                {toast.message}
                            </p>
                        </div>

                        <button
                            onClick={() => removeToast(toast.id)}
                            className={`p-1 rounded-lg transition-colors
                                ${toast.type === 'milestone' ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}
                            `}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
